import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import { ProcessedFareRule } from '@/src/types/fareFamilyInfo'
import { PaxWiseBaggages } from '@/src/types/itinerary'
import { TranslationFunction } from '@/src/types/translations'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Typography } from '@mui/material'
import TimelineWrapper from '../timeline'
import BaggageTable from './baggageTable'

// Type for raw baggage from API
interface RawBaggageItem {
    baggageType: 'CARRY_ON' | 'CHECK_IN'
    baggageTypeDisplayText: string
    baggageDescription: string | null
    permitted: boolean
    baggageAllowances: Array<{
        unit: string | null
        qty: number
        pieceInfo: number
        description: string
    }>
}

interface FareRulesDrawerProps {
    processedFareRules: ProcessedFareRule[]
    baggageData: {
        type: 'CARRY_ON' | 'CHECK_IN'
        value: PaxWiseBaggages
    }[]
    price: string
    fareTitle: string
    isParentOpen?: boolean
    onClose: () => void
    rawBaggage?: RawBaggageItem[] // Optional prop for SRP fare family with proper type
    t: TranslationFunction<'common'>
    isPerPaxPriceEnabled?: boolean
}

const FareRulesDrawer = ({
    processedFareRules,
    baggageData,
    price,
    fareTitle,
    isParentOpen = true,
    onClose,
    rawBaggage,
    t,
    isPerPaxPriceEnabled
}: FareRulesDrawerProps) => {
    return (
        <Box display="flex" flexDirection="column" height="100%">
            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    paddingTop: 0,
                    borderBottom: '1px solid',
                    borderColor: 'borders.default',
                    backgroundColor: 'background.paper'
                }}
            >
                {/* Left section - Close icon */}
                <Box sx={{ width: 80, display: 'flex', justifyContent: 'flex-start' }}>
                    <IconButton size="small" sx={{ padding: 1 }} onClick={onClose}>
                        <CloseIcon sx={{ fontSize: '20px', color: 'textColors.primary' }} />
                    </IconButton>
                </Box>

                {/* Center section - Fare title */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            color: 'textColors.primary',
                            fontSize: '16px',
                            textAlign: 'center'
                        }}
                    >
                        {fareTitle}
                    </Typography>
                </Box>

                {/* Right section - Price */}
                <Box sx={{ width: 80, display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            color: 'textColors.primary',
                            fontSize: '14px'
                        }}
                    >
                        {price} {isPerPaxPriceEnabled && <PerAdultLabel />}
                    </Typography>
                </Box>
            </Box>

            {/* Content - Scrollable */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto'
                }}
            >
                {(baggageData.length > 0 || (rawBaggage && rawBaggage.length > 0)) && (
                    <Box sx={{ px: 1.5, py: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{t('baggagePolicy')}</Typography>
                        <BaggageTable baggageData={baggageData} rawBaggage={rawBaggage} />
                    </Box>
                )}
                {processedFareRules.map((fareRule, index) => {
                    const { type, timeSegments, priceSegments, segmentCount, title } = fareRule

                    return (
                        <Box key={index}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 1.5,
                                    py: 2
                                }}
                            >
                                <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>{title}</Typography>
                            </Box>
                            {isEmpty(fareRule) || !(timeSegments.length > 0 && priceSegments.length > 0) ? (
                                <Box sx={{ px: 3, py: 2 }}>
                                    <Typography sx={{ fontSize: '14px', color: '#6b7280', textAlign: 'center' }}>
                                        {t('noPolicyInformationAvailable')}
                                    </Typography>
                                </Box>
                            ) : (
                                <TimelineWrapper
                                    type={type}
                                    timeSegments={timeSegments}
                                    priceSegments={priceSegments}
                                    segmentCount={segmentCount}
                                    isParentOpen={isParentOpen}
                                />
                            )}
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}

export default FareRulesDrawer
